import { acceptLanguageHeader, DEFAULT_LOCALE, messagesFor, type Locale } from "@/locale";
import {
  ApiClientError,
  type AccountSession,
  type ApiErrorBody,
  type AdminCabinet,
  type AuditPage,
  type CapacityProduct,
  type CapacityRequest,
  type Dashboard,
  type EnrollmentReceipt,
  type Health,
  type Integration,
  type IntegrationDeleteResult,
  type IntegrationProvider,
  type ProjectActivity,
  type ProjectActivityWindow,
  type ProjectConnection,
  type ProjectConnectionsPage,
  type ProjectServicesPage,
  type ProjectAlertsPage,
  type ProjectAlertPreferences,
  type ProjectAlertAcknowledgeResult,
  type OperatorWorkspaceRequestPage,
  type WorkspaceProfilesPage,
  type WorkspaceRequest,
  type WorkspaceRequestCreate,
  type WorkspaceRequestPage,
  type WorkspaceRequestPlanResponse,
  type WorkspaceRequestState,
  type Lease,
  type Machine,
  type Operation,
  type OperatorOverview,
  type Project,
  type PasswordChangeResult,
  type SessionPayload,
  type Task,
  type TaskEventsResponse,
  type TaskKind,
  type TaskListPage,
  type TaskListQuery,
  type TenantEnvironment,
} from "./types";

const BASE = "/api/v1";

type CsrfProvider = () => string | null | undefined;
type LocaleHeaderProvider = () => string;

let csrfProvider: CsrfProvider = () => null;
let localeHeaderProvider: LocaleHeaderProvider = () => acceptLanguageHeader(DEFAULT_LOCALE);
let activeLocale: Locale = DEFAULT_LOCALE;

export function setCsrfProvider(provider: CsrfProvider): void {
  csrfProvider = provider;
}

export function setLocaleProvider(provider: LocaleHeaderProvider): void {
  localeHeaderProvider = provider;
  const header = provider();
  activeLocale = header.startsWith("de") ? "de" : "en";
}

function clientMessage(code: keyof ReturnType<typeof messagesFor>["api"], status?: number): string {
  const api = messagesFor(activeLocale).api;
  if (code === "httpError") {
    return api.httpError.replace("{status}", String(status ?? ""));
  }
  return api[code];
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiClientError(res.status, "invalid_json", clientMessage("invalidJson"), undefined, true);
  }
}

function toError(status: number, body: unknown): ApiClientError {
  const err = (body as ApiErrorBody | null)?.error;
  if (err?.message) {
    return new ApiClientError(
      status,
      err.code || "error",
      err.message,
      err.request_id,
      Boolean(err.retryable),
    );
  }
  if (status === 401) {
    return new ApiClientError(401, "unauthenticated", clientMessage("sessionExpired"));
  }
  if (status === 403) {
    return new ApiClientError(403, "forbidden", clientMessage("forbidden"));
  }
  if (!navigator.onLine) {
    return new ApiClientError(0, "offline", clientMessage("offline"), undefined, true);
  }
  return new ApiClientError(status, "http_error", clientMessage("httpError", status), undefined, status >= 500);
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  auth?: boolean;
};

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": localeHeaderProvider(),
  };

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (method !== "GET" && method !== "HEAD") {
    const csrf = csrfProvider();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  if (options.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: "include",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch {
    throw new ApiClientError(
      0,
      navigator.onLine ? "network_error" : "offline",
      navigator.onLine ? clientMessage("network") : clientMessage("offline"),
      undefined,
      true,
    );
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw toError(res.status, body);
  }
  return body as T;
}

export const api = {
  health: () => apiRequest<Health>("/health", { auth: false }),

  session: () => apiRequest<SessionPayload>("/session"),

  register: (payload: {
    name: string;
    email: string;
    password: string;
    organization: string;
  }) => apiRequest<SessionPayload>("/auth/register", { method: "POST", body: payload }),

  login: (payload: { email: string; password: string }) =>
    apiRequest<SessionPayload>("/auth/login", { method: "POST", body: payload }),

  logout: () => apiRequest<Record<string, never>>("/auth/logout", { method: "POST", body: {} }),

  dashboard: () => apiRequest<Dashboard>("/dashboard"),

  machines: () => apiRequest<{ items: Machine[] }>("/machines"),

  projects: () => apiRequest<{ items: Project[] }>("/projects"),

  createProject: (payload: { name: string; description: string }) =>
    apiRequest<Project>("/projects", { method: "POST", body: payload }),

  project: (id: string) => apiRequest<Project>(`/projects/${id}`),

  projectActivity: (
    projectId: string,
    query: { window?: ProjectActivityWindow } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (query.window && query.window !== "24h") params.set("window", query.window);
    const qs = params.toString();
    return apiRequest<ProjectActivity>(
      `/projects/${projectId}/activity${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },

  projectServices: (projectId: string, signal?: AbortSignal) =>
    apiRequest<ProjectServicesPage>(`/projects/${projectId}/services`, { signal }),

  projectAlerts: (projectId: string, signal?: AbortSignal) =>
    apiRequest<ProjectAlertsPage>(`/projects/${projectId}/alerts`, { signal }),

  setProjectAlertPreferences: (projectId: string, enabled: boolean) =>
    apiRequest<ProjectAlertPreferences>(`/projects/${projectId}/alerts/preferences`, {
      method: "POST",
      body: { enabled },
    }),

  acknowledgeProjectAlert: (projectId: string, alertId: string) =>
    apiRequest<ProjectAlertAcknowledgeResult>(
      `/projects/${projectId}/alerts/${alertId}/acknowledge`,
      { method: "POST", body: {} },
    ),

  projectConnections: (projectId: string, signal?: AbortSignal) =>
    apiRequest<ProjectConnectionsPage>(`/projects/${projectId}/connections`, { signal }),

  attachProjectConnection: (projectId: string, integrationId: string) =>
    apiRequest<ProjectConnection>(`/projects/${projectId}/connections`, {
      method: "POST",
      body: { integration_id: integrationId },
    }),

  detachProjectConnection: (projectId: string, integrationId: string) =>
    apiRequest<{ ok: true }>(`/projects/${projectId}/connections/${integrationId}`, {
      method: "DELETE",
      body: {},
    }),

  tasks: (query: TaskListQuery = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    const q = query.q?.trim();
    if (q) params.set("q", q);
    if (query.state) params.set("state", query.state);
    if (query.project_id) params.set("project_id", query.project_id);
    if (query.page != null && query.page > 1) params.set("page", String(query.page));
    if (query.limit != null && query.limit > 0 && query.limit !== 25) {
      params.set("limit", String(query.limit));
    }
    const qs = params.toString();
    return apiRequest<TaskListPage>(`/tasks${qs ? `?${qs}` : ""}`, { signal });
  },

  task: (id: string, signal?: AbortSignal) =>
    apiRequest<Task>(`/tasks/${id}`, { signal }),

  taskEvents: (id: string, signal?: AbortSignal) =>
    apiRequest<TaskEventsResponse>(`/tasks/${id}/events`, { signal }),

  createTask: (
    projectId: string,
    payload: { title: string; instructions: string; kind: TaskKind },
    idempotencyKey: string,
  ) =>
    apiRequest<Operation>(`/projects/${projectId}/tasks`, {
      method: "POST",
      body: payload,
      idempotencyKey,
    }),

  cancelTask: (id: string, idempotencyKey: string) =>
    apiRequest<Operation>(`/tasks/${id}/cancel`, {
      method: "POST",
      body: {},
      idempotencyKey,
    }),

  leases: () => apiRequest<{ items: Lease[] }>("/leases"),

  createLease: (machineId: string, idempotencyKey: string) =>
    apiRequest<Operation>("/leases", {
      method: "POST",
      body: { machine_id: machineId },
      idempotencyKey,
    }),

  endLease: (id: string, idempotencyKey: string) =>
    apiRequest<Operation>(`/leases/${id}/end`, {
      method: "POST",
      body: {},
      idempotencyKey,
    }),

  operation: (id: string, signal?: AbortSignal) =>
    apiRequest<Operation>(`/operations/${id}`, { signal }),

  account: () => apiRequest<SessionPayload>("/account"),

  updateProfile: (payload: { name: string; organization?: string }) =>
    apiRequest<SessionPayload>("/account/profile", { method: "PATCH", body: payload }),

  changePassword: (payload: { current_password: string; new_password: string }) =>
    apiRequest<PasswordChangeResult>("/account/password", { method: "POST", body: payload }),

  sessions: () => apiRequest<{ items: AccountSession[] }>("/account/sessions"),

  revokeSession: (id: string) =>
    apiRequest<Record<string, never>>(`/account/sessions/${id}`, {
      method: "DELETE",
      body: {},
    }),

  revokeOtherSessions: () =>
    apiRequest<{ ok: true; revoked: number }>("/account/sessions/revoke-others", {
      method: "POST",
      body: {},
    }),

  environment: () => apiRequest<TenantEnvironment>("/environment"),

  createCapacityRequest: (
    payload: { product: CapacityProduct; amount_gb: number; notes?: string },
    idempotencyKey: string,
  ) =>
    apiRequest<CapacityRequest>("/environment/requests", {
      method: "POST",
      body: payload,
      idempotencyKey,
    }),

  cancelCapacityRequest: (id: string) =>
    apiRequest<CapacityRequest>(`/environment/requests/${id}/cancel`, {
      method: "POST",
      body: {},
    }),

  workspaceProfiles: (signal?: AbortSignal) =>
    apiRequest<WorkspaceProfilesPage>("/workspace-profiles", { signal }),

  projectWorkspaceRequests: (
    projectId: string,
    query: { page?: number; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (query.page != null && query.page > 1) params.set("page", String(query.page));
    if (query.limit != null && query.limit > 0 && query.limit !== 10) {
      params.set("limit", String(query.limit));
    }
    const qs = params.toString();
    return apiRequest<WorkspaceRequestPage>(
      `/projects/${projectId}/workspace-requests${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },

  createWorkspaceRequest: (
    projectId: string,
    payload: WorkspaceRequestCreate,
    idempotencyKey: string,
  ) =>
    apiRequest<WorkspaceRequest>(`/projects/${projectId}/workspace-requests`, {
      method: "POST",
      body: payload,
      idempotencyKey,
    }),

  cancelWorkspaceRequest: (id: string, payload: { version: number }) =>
    apiRequest<WorkspaceRequest>(`/workspace-requests/${id}/cancel`, {
      method: "POST",
      body: payload,
    }),

  workspaceRequestPlan: (id: string, signal?: AbortSignal) =>
    apiRequest<WorkspaceRequestPlanResponse>(`/workspace-requests/${id}/plan`, { signal }),

  operatorWorkspaceRequests: (
    query: { page?: number; limit?: number; state?: WorkspaceRequestState } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (query.page != null && query.page > 1) params.set("page", String(query.page));
    if (query.limit != null && query.limit > 0 && query.limit !== 10) {
      params.set("limit", String(query.limit));
    }
    if (query.state) params.set("state", query.state);
    const qs = params.toString();
    return apiRequest<OperatorWorkspaceRequestPage>(
      `/operator/workspace-requests${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },

  reviewWorkspaceRequest: (
    id: string,
    payload: { version: number; state: "reviewing" | "declined"; review_note: string },
  ) =>
    apiRequest<WorkspaceRequest>(`/operator/workspace-requests/${id}`, {
      method: "PATCH",
      body: payload,
    }),

  operatorWorkspaceRequestPlan: (id: string, signal?: AbortSignal) =>
    apiRequest<WorkspaceRequestPlanResponse>(`/operator/workspace-requests/${id}/plan`, {
      signal,
    }),

  operatorOverview: () => apiRequest<OperatorOverview>("/operator/overview"),

  adminCabinet: () => apiRequest<AdminCabinet>("/admin/cabinet"),

  enrollMachine: (payload: { display_name: string; serial: string; purpose: "dedicated-test" | "rental" }) =>
    apiRequest<EnrollmentReceipt>("/operator/machines/enroll", { method: "POST", body: payload }),

  setMaintenance: (machineId: string, payload: { enabled: boolean; reason: string }) =>
    apiRequest<Machine>(`/operator/machines/${machineId}/maintenance`, {
      method: "POST",
      body: payload,
    }),

  audit: () => apiRequest<AuditPage>("/audit"),

  integrations: () => apiRequest<{ items: Integration[] }>("/integrations"),

  createIntegration: (payload: {
    name: string;
    provider: IntegrationProvider;
    secret: string;
  }) => apiRequest<Integration>("/integrations", { method: "POST", body: payload }),

  verifyIntegration: (id: string) =>
    apiRequest<Integration>(`/integrations/${id}/verify`, { method: "POST", body: {} }),

  rotateIntegration: (id: string, payload: { secret: string; version: number }) =>
    apiRequest<Integration>(`/integrations/${id}`, { method: "PATCH", body: payload }),

  deleteIntegration: (id: string) =>
    apiRequest<IntegrationDeleteResult>(`/integrations/${id}`, {
      method: "DELETE",
      body: {},
    }),
};
